import { createApi } from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import { Auth } from '@aws-amplify/auth';
import { createOptimisticUpdateHandler, defaultConfigs } from '../../utils/cacheHelpers';

// Create fresh axios instance for notifications
const notificationAxios = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "https://5twmjprh12.execute-api.us-east-1.amazonaws.com/dev",
});

// Add token interceptor
notificationAxios.interceptors.request.use(
  async (config) => {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.log('No auth session found:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Custom base query that uses our fresh axios instance
const axiosBaseQuery = () =>
  async ({ url, method, data, params, headers }) => {
    try {
      const result = await notificationAxios({
        url: url,
        method: method,
        data: data,
        params: params,
        headers: headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

// Base API
export const baseApi = createApi({
  reducerPath: 'notificationApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Notification'],
  endpoints: () => ({}),
});

// Notification Actions
export const NotificationAction = {
  MARK_READ: 'mark_read',
  MARK_UNREAD: 'mark_unread',
  DELETE: 'delete',
  ARCHIVE: 'archive',
};

// Notification Status
export const NotificationStatus = {
  READ: 'read',
  UNREAD: 'unread',
  ARCHIVED: 'archived',
};

export const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: (params = {}) => ({
        url: '/account/notification/list',
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          status: params.status || undefined,
          type: params.type || undefined,
          sortBy: params.sortBy || 'created_at',
          sortOrder: params.sortOrder || 'DESC',
          ...params,
        },
      }),
      providesTags: [{ type: 'Notification', id: 'NOTIFICATIONS' }],
    }),

    getNotificationStats: builder.query({
      query: () => ({
        url: '/account/notification/stats',
        method: 'GET',
      }),
      providesTags: [{ type: 'Notification', id: 'STATS' }],
    }),

    getNotification: builder.query({
      query: (id) => `/account/notification/${id}`,
      providesTags: (result, error, id) => [{ type: 'Notification', id }],
    }),

    getUnreadCount: builder.query({
      query: () => '/account/notification/unread-count',
      providesTags: [{ type: 'Notification', id: 'STATS' }],
    }),

    createNotification: builder.mutation({
      query: (notification) => ({
        url: '/account/notification/create',
        method: 'POST',
        data: notification,
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'NOTIFICATIONS' },
        { type: 'Notification', id: 'STATS' },
      ],
    }),

    updateNotification: builder.mutation({
      query: (params) => ({
        url: '/account/notification/update',
        method: 'POST',
        data: params,
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        const cacheConfig = defaultConfigs.forNotifications(notificationApi);
        const handler = createOptimisticUpdateHandler(cacheConfig);
        await handler(payload, { dispatch, queryFulfilled, getState });
      },
      invalidatesTags: [
        { type: 'Notification', id: 'NOTIFICATIONS' },
        { type: 'Notification', id: 'STATS' },
      ],
    }),

    markAsRead: builder.mutation({
      query: (id) => ({
        url: '/account/notification/update',
        method: 'POST',
        data: {
          id: id,
          action: NotificationAction.MARK_READ,
        },
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const payload = { id, action: NotificationAction.MARK_READ };
        const cacheConfig = defaultConfigs.forNotifications(notificationApi);
        const handler = createOptimisticUpdateHandler(cacheConfig);
        await handler(payload, { dispatch, queryFulfilled, getState });
      },
      invalidatesTags: [{ type: 'Notification', id: 'STATS' }],
    }),

    markAllAsRead: builder.mutation({
      query: () => ({
        url: '/account/notification/update',
        method: 'POST',
        data: {
          action: NotificationAction.MARK_READ,
          all: true,
        },
      }),
      invalidatesTags: [
        { type: 'Notification', id: 'NOTIFICATIONS' },
        { type: 'Notification', id: 'STATS' },
      ],
    }),

    deleteNotification: builder.mutation({
      query: (id) => ({
        url: '/account/notification/update',
        method: 'POST',
        data: {
          id: id,
          action: NotificationAction.DELETE,
        },
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // For delete, we need custom logic to remove from list
        const patches = [];
        
        // Remove from all list queries in cache
        try {
          const state = getState();
          const listQueries = notificationApi.util.selectCachedArgsForQuery(state, 'getNotifications');
          
          listQueries.forEach((queryArgs) => {
            const listPatch = dispatch(
              notificationApi.util.updateQueryData('getNotifications', queryArgs, (draft) => {
                if (draft.notifications) {
                  draft.notifications = draft.notifications.filter((n) => n.id !== id);
                }
              })
            );
            patches.push(listPatch);
          });
        } catch (error) {
          console.log('Error updating cache on delete:', error);
        }

        try {
          await queryFulfilled;
        } catch {
          // Revert on failure
          patches.forEach(patch => patch.undo());
        }
      },
      invalidatesTags: [{ type: 'Notification', id: 'STATS' }],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationStatsQuery,
  useGetNotificationQuery,
  useGetUnreadCountQuery,
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useLazyGetNotificationsQuery,
  useLazyGetNotificationStatsQuery,
} = notificationApi; 